/**
 * 升档提示的判据(批 B0,2026-08-05)——从 VideoCreatePage 的内联 JSX 抽出来,理由有两条:
 *
 * 1. **它漏了一条腿**。ai 侧 `mode_router.chunk_needs_seedance` 认两件事会升 Seedance 计费:
 *    ① 段内任一镜带 identity/product/motion 参考;② 段内任一镜 `speechMode=lip_sync` 且有口播词。
 *    而页面只判了第 ①,于是一个**只有 lip_sync、没有任何参考**的段会静默升档扣费、蓝条不亮 ——
 *    「计费提前说清」这条自律当场破功。这是存量洞(导演本来就会自发产 lip_sync:08-04 真实原文
 *    基线 lanxin-yinliu 就有 1/5),2026-08-05 批 A 的出镜兜底会让它更常发生。
 * 2. 内联在 JSX 里的判据没法单测,漏腿这种事只能靠人读出来 —— 抽成纯函数后由测试锁死。
 *
 * ★判据必须与 ai 侧同源:改这里之前先看 `app/modules/video/application/mode_router.py`
 *   的 `chunk_needs_seedance`,别让「教的和查的不是同一件事」再犯一次。
 */
import type { VideoShotReference } from "../api/videoService";

/** ai 侧 `mode_router._SEEDANCE_ROLES` 同源(1005 补齐 reenact/scene,与 ai 逐项一致)。 */
const SEEDANCE_ROLES = new Set(["identity", "product", "motion", "reenact", "scene"]);

export interface UpgradeNoticeInput {
  references?: (VideoShotReference[] | null)[] | null;
  speechModes?: (string | null)[] | null;
  voiceovers?: (string | null)[] | null;
  characterId?: number | string | null;
  characterIdentityKeys?: string[] | null;
  panelScenes?: string[] | null;
}

/** 有镜带 identity/product/motion 参考(逐镜派活 or 选人自动挂)。 */
function hasReferenceLeg(form: UpgradeNoticeInput): boolean {
  const perShot = (form.references ?? []).some((refs) =>
    (refs ?? []).some((r) => SEEDANCE_ROLES.has(r?.role))
  );
  const pickedCharacter =
    !!form.characterId &&
    (form.characterIdentityKeys?.length ?? 0) > 0 &&
    (form.panelScenes?.length ?? 0) > 0;
  return perShot || pickedCharacter;
}

/**
 * 有镜是「人物对镜开口」(lip_sync 且有口播词)——ai 侧 `chunk_needs_seedance` 的第二条腿。
 *
 * ★空 lip_sync 不算:口型对齐没有词 = 没人在说话,ai 侧同一口径
 *   (`s.speech_mode == "lip_sync" and (s.voiceover or "").strip()`)。
 */
function hasLipSyncLeg(form: UpgradeNoticeInput): boolean {
  const modes = form.speechModes ?? [];
  const vos = form.voiceovers ?? [];
  return modes.some((m, i) => m === "lip_sync" && (vos[i] ?? "").trim().length > 0);
}

/** 本单会不会触发按段升档(= 该不该把涨价说给用户听)。 */
export function willUpgradeSomeChunk(form: UpgradeNoticeInput): boolean {
  return hasReferenceLeg(form) || hasLipSyncLeg(form);
}

/**
 * 蓝条正文该怎么说 —— 只有参考 / 只有对镜讲解 / 两者都有,说法不一样。
 * 用户看到「已选人物/动作参考」却什么都没选过,只会觉得界面在胡说。
 */
export function upgradeNoticeSubject(form: UpgradeNoticeInput, autoMotionCount: number): string {
  const refs = hasReferenceLeg(form);
  const lip = hasLipSyncLeg(form);
  if (refs && lip) {
    return autoMotionCount > 0
      ? "本单含 AI 自动配的动作参考(逐镜卡可换可清)与人物对镜讲解镜"
      : "本单含人物/动作参考与人物对镜讲解镜";
  }
  if (lip) {
    return "本单含人物对镜讲解镜(speechMode 口型对齐)";
  }
  if (autoMotionCount > 0) return "本单含 AI 自动配的动作参考(逐镜卡可换可清)";
  // ★1005 生产实测:只传了一张产品图、没选人物/动作,导演把产品图逐镜挂成 product 参考,
  //   蓝条却说「已选人物/动作参考」—— 用户没选过。按实际挂的参考种类说。
  return `本单含${referenceKinds(form)}参考`;
}

/** 本单实际挂了哪几类参考(人话),顺序固定:人物 / 产品图 / 动作 / 场景。 */
function referenceKinds(form: UpgradeNoticeInput): string {
  const roles = new Set<string>();
  for (const refs of form.references ?? []) {
    for (const r of refs ?? []) if (r?.role) roles.add(r.role);
  }
  const picked =
    !!form.characterId &&
    (form.characterIdentityKeys?.length ?? 0) > 0 &&
    (form.panelScenes?.length ?? 0) > 0;
  const parts: string[] = [];
  if (roles.has("identity") || picked) parts.push("人物");
  if (roles.has("product")) parts.push("产品图");
  if (roles.has("motion") || roles.has("reenact")) parts.push("动作");
  if (roles.has("scene")) parts.push("场景");
  return parts.length > 0 ? parts.join("/") : "人物/动作";
}

/**
 * 蓝条里「怎么计费」那半句(1005)。
 *
 * ★实扣口径(api origin/main 核实):首次出片 = 建单时绑的报价单按单兑现
 *   (`QuoteSettlement#resolve`),结算前核对用的是任务 payload 顶层 model/resolution
 *   (`VideoRenderSpecSource`)= 用户选的档,**不是** ai 段级升档后的实际引擎。
 *   所以升档段照报价扣、不加价;旧句「该部分按『综合』档计费(略高于当前…)」与实扣不符。
 */
export const UPGRADE_BILLING_NOTE = "计费仍按本次报价(右侧预计扣费)结算,不因换引擎加价";
